import axios from 'axios';

const formatDateStr = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const todayString = () => formatDateStr(new Date());

// created_at comes back as a full ISO timestamp string; read local date parts rather than
// splitting the UTC string, which can land on the wrong calendar day depending on timezone.
const localDateOnly = (isoTimestamp) => {
  if (!isoTimestamp) return '';
  const date = new Date(isoTimestamp);
  if (isNaN(date.getTime())) return '';
  return formatDateStr(date);
};

// Finds the first date after endDate that isn't a weekend or a national holiday
function computeFirstWorkingDayAfter(endDateStr, holidayDates) {
  const [y, m, d] = endDateStr.split('-').map(Number);
  const cursor = new Date(y, m - 1, d);
  cursor.setDate(cursor.getDate() + 1);

  for (let i = 0; i < 60; i++) {
    const dow = cursor.getDay();
    const dateStr = formatDateStr(cursor);
    if (dow !== 0 && dow !== 6 && !holidayDates.has(dateStr)) {
      return dateStr;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return formatDateStr(cursor);
}

// Builds the {{...}} -> value map for a PTO decision ("Rešenje") document for a specific approved request.
// `employee` needs { id, first_name, last_name }; `formatDate` formats a 'YYYY-MM-DD' string for display.
// `printDate` (optional 'YYYY-MM-DD') overrides the {{print_date}} placeholder, which otherwise defaults to today.
// `approverName` (optional) fills {{approver_name}} - resolved by the caller since it depends on
// which endpoint fetched the request (only the admin "view others" endpoint joins the reviewer's name).
// `ptoYear` (optional) overrides {{pto_year}}, which otherwise defaults to the previous year if
// carried-over days are available, or the leave year otherwise.
export async function buildPtoDecisionPlaceholders({ salonId, request, employee, formatDate, printDate, approverName, ptoYear }) {
  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const year = parseInt(request.start_date.split('-')[0]);
  const endYear = parseInt(request.end_date.split('-')[0]);
  const holidayYears = year === endYear ? [year] : [year, endYear];

  const [balanceRes, weeklyHoursRes, ...holidayResponses] = await Promise.all([
    axios.get(`/api/salons/${salonId}/pto/balance`, { ...config, params: { userId: employee.id, year } }),
    axios.get(`/api/salons/${salonId}/weekly-hours`, { ...config, params: { userId: employee.id } }),
    ...holidayYears.map((y) => axios.get(`/api/salons/${salonId}/holidays`, { ...config, params: { year: y } }))
  ]);

  const holidayDates = new Set();
  holidayResponses.forEach((r) => (r.data.holidays || []).forEach((h) => holidayDates.add(h.holiday_date)));

  const firstWorkingDayAfter = computeFirstWorkingDayAfter(request.end_date, holidayDates);

  const allocatedDays = balanceRes.data.allocatedDays;
  const carriedOverDays = balanceRes.data.carriedOverDays || 0;
  const availableDays = balanceRes.data.availableDays;
  const weeklyHours = weeklyHoursRes.data.weeklyHours;

  const fmt = formatDate || ((d) => d);
  const defaultPtoYear = carriedOverDays > 0 ? year - 1 : year;

  return {
    '{{employee_name}}': `${employee.first_name || ''} ${employee.last_name || ''}`.trim(),
    '{{employee_first_name}}': employee.first_name || '',
    '{{employee_last_name}}': employee.last_name || '',
    '{{job_title}}': employee.position || '',
    '{{year}}': String(year),
    '{{pto_year}}': String(ptoYear !== undefined && ptoYear !== null && ptoYear !== '' ? ptoYear : defaultPtoYear),
    '{{days_count}}': String(Math.round(Number(request.days_count))),
    '{{start_date}}': fmt(request.start_date),
    '{{end_date}}': fmt(request.end_date),
    '{{first_working_day_after}}': fmt(firstWorkingDayAfter),
    '{{reason}}': request.reason || '',
    '{{request_date}}': fmt(localDateOnly(request.created_at)),
    '{{approver_name}}': approverName || '',
    '{{print_date}}': fmt(printDate || todayString()),
    '{{allocated_days}}': allocatedDays === null || allocatedDays === undefined ? '' : String(allocatedDays),
    '{{carried_over_days}}': String(carriedOverDays),
    '{{weekly_hours}}': weeklyHours === null || weeklyHours === undefined ? '' : String(weeklyHours),
    '{{available_days_after}}': availableDays === null || availableDays === undefined ? '' : String(availableDays)
  };
}
